import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { apiErrorMessage, formatApiMessage } from '@/lib/api-error'
import { formatLocaleDateTime } from '@/lib/format-utils'
import { lastEmailEvent, readNudgeErrors } from '@/lib/onboarding'

interface NudgeTarget {
  propertyId: string
  displayName: string
  ownerEmails: string[]
}

interface NudgeDialogProps {
  target: NudgeTarget | null
  onClose: () => void
}

interface LastEmailProps {
  events: readonly { type: string; at: string }[] | null | undefined
  checking: boolean
  failed: boolean
}

function LastEmail({ events, checking, failed }: LastEmailProps) {
  if (checking) return <LoadingSkeleton className="h-4 w-48" aria-label="Checking the last email" />
  if (failed)
    return <p className="text-sm text-textMuted">The last email date could not be loaded.</p>
  const last = lastEmailEvent(events)
  if (!last)
    return <p className="text-sm text-textMuted">No onboarding email has reached the owner yet.</p>
  return (
    <p className="text-sm text-textMuted">
      {last.type === 'nudged' ? 'Last nudged' : 'Invited'} {formatLocaleDateTime(last.at)}.
    </p>
  )
}

/**
 * Confirms a nudge (CAP-1467): re-sends the onboarding email to the
 * property's owners. Manual only, never scheduled; it says when the last one
 * went so a second click a minute later is a decision, not an accident.
 */
export function NudgeDialog({ target: openTarget, onClose }: NudgeDialogProps) {
  // Keep the last target on screen while the dialog animates closed, so its
  // text does not change in the closing frames.
  const [shown, setShown] = useState<NudgeTarget | null>(openTarget)
  if (openTarget && openTarget !== shown) setShown(openTarget)
  const target = openTarget ?? shown
  // Re-read on every open, and show nothing as current until that read is
  // back: the date exists to stop a second email a minute after someone
  // else's, so a cached property is not good enough, even for a moment.
  const lastRead = useQuery({
    queryKey: ['property', openTarget?.propertyId],
    queryFn: () => api.getProperty(openTarget?.propertyId ?? ''),
    enabled: openTarget !== null,
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const checking = openTarget !== null && lastRead.isFetching
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const close = () => {
    if (sending) return
    setError(null)
    onClose()
  }

  const send = async () => {
    if (!target || sending) return
    setSending(true)
    setError(null)
    try {
      const result = await api.nudgeOnboarding(target.propertyId)
      const failures = readNudgeErrors(result)
      // admin-api names who it reached, not who it missed; the missed owners
      // are the rest of the list this dialog was opened with.
      const reached = new Set(result.sent_to.map(email => email.toLowerCase()))
      const missed = target.ownerEmails.filter(email => !reached.has(email.toLowerCase()))
      const reasons = failures.map(f => formatApiMessage(f.code, f.message, f.code)).join('; ')
      const sentLine = result.sent_to.length
        ? `Sent to ${result.sent_to.join(', ')}.`
        : 'No owner was emailed.'
      const missedLine = failures.length
        ? ` Not sent to ${missed.length ? missed.join(', ') : `${failures.length} other owner${failures.length === 1 ? '' : 's'}`}: ${reasons}`
        : ''
      // Default style even when some owners were missed: an email did go,
      // and a red toast reads as "nothing happened" and invites a resend.
      toast({
        title: !result.sent_to.length
          ? 'No email was sent'
          : failures.length
            ? 'Sent to some owners only'
            : 'Onboarding email sent',
        description: sentLine + missedLine,
        variant: result.sent_to.length ? undefined : 'destructive',
      })
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] })
      void queryClient.invalidateQueries({ queryKey: ['property', target.propertyId] })
      setSending(false)
      onClose()
    } catch (err) {
      setError(apiErrorMessage(err, 'The email could not be sent. Please try again.'))
      setSending(false)
    }
  }

  return (
    <Dialog open={openTarget !== null} onOpenChange={open => !open && close()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nudge the owner?</DialogTitle>
          <DialogDescription>
            Sends the onboarding email
            {target?.ownerEmails.length ? ` to ${target.ownerEmails.join(', ')}` : ''}, with the
            link to {target?.displayName ?? 'the property'}&apos;s profile.
          </DialogDescription>
        </DialogHeader>
        {openTarget && (
          <LastEmail
            events={lastRead.data?.onboarding_events}
            checking={checking}
            failed={lastRead.isError}
          />
        )}
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={close} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={() => void send()} disabled={sending || checking}>
            {sending ? 'Sending...' : 'Send email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export type { NudgeTarget }
